<template>
  <div>
    <Heading v-if="title" level="h6" class="mb-3 !mt-0">{{ title }}</Heading>
    <div v-if="hasData" :style="{ height: `${height}px` }">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <EmptyState 
      v-else
      icon="chart" 
      :message="emptyMessage" 
      :height="`h-[${height}px]`"
      :show-action="showAction"
      :action-text="actionText"
      :action-icon="actionIcon"
      :action-variant="actionVariant"
      :action-size="actionSize"
      @action="handleAction"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Chart, BarController, BarElement, LinearScale, CategoryScale, Title, Tooltip, Legend } from 'chart.js'
import Heading from '@/components/ui/Heading.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import { t } from '@/i18n/en-us'

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Title, Tooltip, Legend)

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  height: {
    type: Number,
    default: 300
  },
  title: {
    type: String,
    default: ''
  },
  emptyMessage: {
    type: String,
    default: () => t('reports.noDataAvailable')
  },
  showAction: {
    type: Boolean,
    default: false
  },
  actionText: {
    type: String,
    default: ''
  },
  actionIcon: {
    type: String,
    default: 'plus'
  },
  actionVariant: {
    type: String,
    default: 'primary'
  },
  actionSize: {
    type: String,
    default: 'sm'
  }
})

const emit = defineEmits(['action'])

const chartCanvas = ref(null)
let chartInstance = null

// Computed
const hasData = computed(() => props.data && props.data.length > 0)

const createChart = () => {
  if (!chartCanvas.value || !props.data || props.data.length === 0) return

  const ctx = chartCanvas.value.getContext('2d')
  
  if (chartInstance) {
    chartInstance.destroy()
  }

  // Extract colors from data if available, otherwise use defaults
  const colors = props.data.map(d => d.color).filter(Boolean)
  const defaultColors = [
    'rgba(59, 130, 246, 0.8)',
    'rgba(16, 185, 129, 0.8)',
    'rgba(249, 115, 22, 0.8)',
    'rgba(239, 68, 68, 0.8)',
    'rgba(168, 85, 247, 0.8)'
  ]
  const backgroundColors = colors.length > 0 ? colors : defaultColors

  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: props.data.map(d => d.label),
      datasets: [{
        label: 'Count',
        data: props.data.map(d => d.value),
        backgroundColor: backgroundColors
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    }
  })
}

const handleAction = () => {
  emit('action')
}

onMounted(() => {
  createChart()
})

watch(() => props.data, async () => {
  // Wait for DOM to update (canvas to be re-mounted if it was removed)
  await nextTick()
  createChart()
}, { deep: true })
</script>

